/**
 * The one "something needs you here" pip for the tenant dashboard's tab strip
 * and mobile drawer (COL-368).
 *
 * Before this existed the strip had three hand-rolled pips with three
 * different looks: Support's rose unread counter, Check-in/out's rose "!" from
 * PR #1387, and Contract's smaller flat `bg-destructive` dot that did not
 * pulse. Same meaning, three appearances — so widening the pip to five tabs
 * started by making it one component. Support's classes won, being the ones
 * tenants have seen longest.
 *
 * The white ring (`border-background`) is what separates the pip from the icon
 * underneath it on any background, and the pulse is what distinguishes "you
 * have something to do" from a static decoration. `motion-reduce` turns the
 * animation off without removing the pip.
 */
import { cn } from "@/lib/utils";

export type TabActionPipProps = {
  /**
   * A count renders as a number (capped at "99+"); leave it out for the "!"
   * that every non-countable action uses.
   */
  count?: number;
  /** What a screen reader announces — say what needs doing, not "alert". */
  ariaLabel: string;
  /**
   * The ring colour has to match whatever sits behind the pip, which differs
   * between the muted tab strip, a white card and the primary-filled active
   * drawer row.
   */
  ringClassName?: string;
  className?: string;
  "data-testid"?: string;
};

export function TabActionPip({
  count,
  ariaLabel,
  ringClassName,
  className,
  "data-testid": testId,
}: TabActionPipProps) {
  const showCount =
    typeof count === "number" && Number.isFinite(count) && count > 0;

  return (
    <span
      className={cn(
        "absolute h-[18px] min-w-[18px] px-1 rounded-full bg-rose-500 text-white",
        "border-2 border-background text-[10px] font-bold flex items-center",
        "justify-center leading-none shadow-sm animate-pulse motion-reduce:animate-none",
        ringClassName,
        className
      )}
      role="status"
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {showCount ? (count > 99 ? "99+" : count) : "!"}
    </span>
  );
}
